import { useRef, useState } from 'react';
import { api as http } from '../lib/api.ts';
import { DatabaseView } from './database.tsx';
import { RichText, childrenOf, useEditor, type EditorApi } from './editor.tsx';
import { item, separator, label as menuLabel, useMenu, type MenuItem } from './menu.tsx';
import { plainOf, safeHref } from './rich.tsx';
import { armBlockDrag } from './drag.ts';
import { createPage, getState, openPage, setSelection, useStore } from './store.ts';
import type { Block, BlockType } from './types.ts';

const TURN_INTO: { type: BlockType; label: string; props?: Record<string, any> }[] = [
  { type: 'text', label: 'Text' },
  { type: 'h1', label: 'Heading 1' },
  { type: 'h2', label: 'Heading 2' },
  { type: 'h3', label: 'Heading 3' },
  { type: 'bulleted', label: 'Bulleted list' },
  { type: 'numbered', label: 'Numbered list' },
  { type: 'todo', label: 'To-do list', props: { checked: false } },
  { type: 'toggle', label: 'Toggle list', props: { open: true } },
  { type: 'quote', label: 'Quote' },
  { type: 'callout', label: 'Callout', props: { icon: '💡' } },
  { type: 'code', label: 'Code', props: { language: 'text' } },
];

/** Blocks that lay their own children out, inside themselves. */
const OWNS_CHILDREN = new Set<BlockType>(['callout']);

export function BlockList({ parentId }: { parentId: string | null }) {
  const api = useEditor();
  const siblings = childrenOf(api.blocks, parentId);
  return (
    <>
      {siblings.map((block, i) => (
        <BlockRow key={block.id} block={block} index={i} siblings={siblings} />
      ))}
    </>
  );
}

function BlockRow({ block, index, siblings }: { block: Block; index: number; siblings: Block[] }) {
  const api = useEditor();
  const [menu, openMenu] = useMenu();
  const rowRef = useRef<HTMLDivElement | null>(null);
  // A string or null, so a drag elsewhere does not re-render this row.
  const dropAt = useStore((s) => (s.dragOverId === block.id ? s.dragWhere : null));
  // A boolean, so a selection elsewhere does not re-render this row.
  const picked = useStore((s) => s.selection.includes(block.id));

  const kids = childrenOf(api.blocks, block.id);
  const open = block.type !== 'toggle' || block.props.open !== false;

  const menuItems = (): MenuItem[] => {
    const chosen = getState().selection;
    const many = chosen.length > 1 && chosen.includes(block.id);
    return [
    item(many ? `Delete ${chosen.length} blocks` : 'Delete',
      () => (many ? api.removeMany(chosen) : api.remove(block)),
      { hint: 'Del', danger: true }),
    item('Duplicate', () => {
      const id = api.insertAfter(block, block.type, block.props);
      api.update(id, { rich: block.rich }, true);
    }),
    separator(),
    menuLabel('Turn into'),
    ...TURN_INTO
      .filter((t) => t.type !== block.type)
      .map((t) => item(t.label, () => {
        for (const id of many ? chosen : [block.id]) api.setType(id, t.type, t.props);
      })),
    ];
  };

  const onHandleDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    // Keep the caret and the page selection where they are; the drag only
    // starts once the pointer has actually travelled.
    e.preventDefault();
    const chosen = getState().selection;
    const ids = chosen.includes(block.id) ? chosen : [block.id];
    armBlockDrag(ids, e.clientX, e.clientY);
  };

  return (
    <div
      ref={rowRef}
      className={`brow brow-${block.type}${picked ? ' picked' : ''}${dropAt ? ` drop-${dropAt}` : ''}`}
    >
      <div className="bgutter" contentEditable={false}>
        <button
          type="button" className="bgutter-btn" aria-label="Add a block below"
          onClick={() => api.insertAfter(block, 'text')}
        >+</button>
        <button
          type="button" className="bgutter-btn handle" aria-label="Block options"
          onMouseDown={onHandleDown}
          onClick={(e) => {
            // Clicking a handle outside the selection starts a new one, so it
            // is always clear what a drag is about to take.
            if (!getState().selection.includes(block.id)) setSelection([block.id]);
            openMenu(e, menuItems());
          }}
        >⠿</button>
      </div>

      <div className="bbody">
        <BlockBody block={block} index={index} siblings={siblings} />
        {kids.length && open && !OWNS_CHILDREN.has(block.type) ? (
          <div className="bkids"><BlockList parentId={block.id} /></div>
        ) : null}
      </div>
      {menu}
    </div>
  );
}

function BlockBody({ block, index, siblings }: { block: Block; index: number; siblings: Block[] }) {
  const api = useEditor();

  switch (block.type) {
    case 'h1': return <h1 className="bh1"><RichText block={block} placeholder="Heading 1" /></h1>;
    case 'h2': return <h2 className="bh2"><RichText block={block} placeholder="Heading 2" /></h2>;
    case 'h3': return <h3 className="bh3"><RichText block={block} placeholder="Heading 3" /></h3>;

    case 'bulleted':
      return (
        <div className="blist">
          <span className="bmark" contentEditable={false}>•</span>
          <RichText block={block} placeholder="List" />
        </div>
      );

    case 'numbered': {
      // Numbering restarts whenever the run of numbered siblings is broken.
      let n = 1;
      for (let i = index - 1; i >= 0 && siblings[i]!.type === 'numbered'; i--) n++;
      return (
        <div className="blist">
          <span className="bmark bnum" contentEditable={false}>{n}.</span>
          <RichText block={block} placeholder="List" />
        </div>
      );
    }

    case 'todo':
      return (
        <div className={`blist btodo${block.props.checked ? ' done' : ''}`}>
          <input
            type="checkbox"
            className="bcheck"
            checked={!!block.props.checked}
            onChange={(e) => api.update(block.id, { props: { ...block.props, checked: e.target.checked } }, true)}
            aria-label={plainOf(block.rich) || 'To-do'}
          />
          <RichText block={block} placeholder="To-do" />
        </div>
      );

    case 'toggle': {
      const open = block.props.open !== false;
      return (
        <div className="blist btoggle">
          <button
            type="button" className={`btwist${open ? ' open' : ''}`} contentEditable={false}
            aria-label={open ? 'Collapse' : 'Expand'} aria-expanded={open}
            onClick={() => api.update(block.id, { props: { ...block.props, open: !open } }, true)}
          >▸</button>
          <RichText block={block} placeholder="Toggle" />
        </div>
      );
    }

    case 'quote':
      return <blockquote className="bquote"><RichText block={block} placeholder="Quote" /></blockquote>;

    case 'callout':
      return (
        <div className={`bcallout tint-${block.props.color ?? 'gray'}`}>
          <IconButton
            value={block.props.icon ?? '💡'}
            onPick={(icon) => api.update(block.id, { props: { ...block.props, icon } }, true)}
          />
          <div className="bcallout-body">
            <RichText block={block} placeholder="Write something…" />
            <BlockList parentId={block.id} />
          </div>
        </div>
      );

    case 'divider':
      return <hr className="bdivider" contentEditable={false} />;

    case 'code':
      return (
        <div className="bcode" contentEditable={false}>
          <div className="bcode-bar">
            <select
              value={block.props.language ?? 'text'}
              onChange={(e) => api.update(block.id, { props: { ...block.props, language: e.target.value } }, true)}
              aria-label="Language"
            >
              {['text', 'javascript', 'typescript', 'python', 'sql', 'json', 'html', 'css', 'bash', 'go', 'rust', 'java']
                .map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <button type="button" onClick={() => void navigator.clipboard?.writeText(plainOf(block.rich))}>
              Copy
            </button>
          </div>
          <pre className="bcode-body">
            <RichText block={block} placeholder="Code" multiline className="mono" />
          </pre>
        </div>
      );

    case 'image': return <ImageBlock block={block} />;
    case 'file': return <FileBlock block={block} />;
    case 'bookmark': return <BookmarkBlock block={block} />;
    case 'page': return <PageLinkBlock block={block} />;
    case 'db': return <DatabaseView dbId={block.props.db_id} />;
    case 'table': return <TableBlock block={block} />;

    default:
      return <RichText block={block} placeholder="Write something, or press / for blocks" />;
  }
}

// ---- the blocks that are not plain text -----------------------------------

function IconButton({ value, onPick }: { value: string; onPick: (icon: string) => void }) {
  const [menu, openMenu] = useMenu();
  const choices = ['💡', '✍️', '📌', '⚠️', '✅', '❓', '🔥', '📋', '⭐', '🧠', '📎', '🎯'];
  return (
    <>
      <button
        type="button" className="bicon" contentEditable={false} aria-label="Change icon"
        onClick={(e) => openMenu(e, choices.map((c) => item(`${c}  ${c === value ? '(current)' : ''}`.trim(), () => onPick(c))))}
      >{value}</button>
      {menu}
    </>
  );
}

function ImageBlock({ block }: { block: Block }) {
  const api = useEditor();
  const src: string = block.props.url || (block.props.file_id ? `/api/files/${block.props.file_id}/download` : '');
  if (!src) return <EmptyMedia block={block} kind="image" />;
  return (
    <figure className="bimage" contentEditable={false}>
      <img src={src} alt={block.props.alt ?? ''} />
      <figcaption>
        <input
          value={block.props.caption ?? ''}
          placeholder="Write a caption…"
          onChange={(e) => api.update(block.id, { props: { ...block.props, caption: e.target.value } }, true)}
        />
      </figcaption>
    </figure>
  );
}

function FileBlock({ block }: { block: Block }) {
  const href: string = block.props.url || (block.props.file_id ? `/api/files/${block.props.file_id}/download` : '');
  if (!href) return <EmptyMedia block={block} kind="file" />;
  return (
    <a className="bfile" href={href} target="_blank" rel="noopener noreferrer" contentEditable={false}>
      <span className="bfile-icon">📎</span>
      <span className="bfile-name">{block.props.name ?? 'File'}</span>
      {block.props.size ? <span className="bfile-size">{humanSize(block.props.size)}</span> : null}
    </a>
  );
}

function BookmarkBlock({ block }: { block: Block }) {
  const href = safeHref(String(block.props.url ?? ''));
  if (!href) return <EmptyMedia block={block} kind="bookmark" />;
  let host = href;
  try { host = new URL(href).host; } catch { /* keep the raw string */ }
  return (
    <a className="bbookmark" href={href} target="_blank" rel="noopener noreferrer" contentEditable={false}>
      <span className="bbookmark-title">{block.props.title || href}</span>
      <span className="bbookmark-host">{host}</span>
    </a>
  );
}

function PageLinkBlock({ block }: { block: Block }) {
  const target = block.props.page_id as string | undefined;
  return (
    <button
      type="button" className="bpagelink" contentEditable={false}
      onClick={() => target && void openPage(target)}
    >
      <span className="bpagelink-icon">{block.props.icon || '📄'}</span>
      <span className="bpagelink-title">{block.props.title || 'Untitled'}</span>
    </button>
  );
}

function TableBlock({ block }: { block: Block }) {
  const api = useEditor();
  const rows: string[][] = Array.isArray(block.props.rows) ? block.props.rows : [];
  const write = (r: number, c: number, value: string) => {
    const next = rows.map((row) => [...row]);
    next[r]![c] = value;
    api.update(block.id, { props: { ...block.props, rows: next } }, true);
  };
  return (
    <div className="btable" contentEditable={false}>
      <table>
        <tbody>
          {rows.map((row, r) => (
            <tr key={r}>
              {row.map((cell, c) => (
                <td key={c}>
                  <input value={cell} onChange={(e) => write(r, c, e.target.value)} aria-label={`Row ${r + 1}, column ${c + 1}`} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EmptyMedia({ block, kind }: { block: Block; kind: 'image' | 'file' | 'bookmark' }) {
  const api = useEditor();
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const upload = async (file: File) => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('post', 'false');
      const res = await http.upload<{ files: any[] }>('/api/files/upload', form);
      const stored = res.files[0];
      if (stored) {
        api.update(block.id, {
          type: stored.kind === 'image' ? 'image' : 'file',
          props: { ...block.props, file_id: stored.id, name: stored.name, size: stored.size },
        }, true);
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="bempty" contentEditable={false}>
      <span className="bempty-icon">{kind === 'image' ? '🖼️' : kind === 'file' ? '📎' : '🔗'}</span>
      <input
        value={url}
        placeholder={kind === 'bookmark' ? 'Paste a link…' : `Paste ${kind} URL, or upload`}
        onChange={(e) => setUrl(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          const href = safeHref(url);
          if (href) api.update(block.id, { props: { ...block.props, url: href } }, true);
        }}
      />
      {kind !== 'bookmark' ? (
        <>
          <button type="button" disabled={busy} onClick={() => fileRef.current?.click()}>
            {busy ? 'Uploading…' : 'Upload'}
          </button>
          <input
            ref={fileRef} type="file" hidden
            accept={kind === 'image' ? 'image/*' : undefined}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }}
          />
        </>
      ) : null}
    </div>
  );
}

function humanSize(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB'];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n < 10 && i > 0 ? n.toFixed(1) : Math.round(n)} ${units[i]}`;
}

/** Used by the slash menu to drop a sub-page into the document. */
export async function insertSubPage(block: Block, api: EditorApi): Promise<void> {
  const parentId = getState().doc?.page.id ?? null;
  const page = await createPage({ parentId, open: false });
  if (!page) return;
  api.update(block.id, {
    type: 'page',
    props: { page_id: page.id, title: page.title || 'Untitled', icon: page.icon },
  }, true);
  await openPage(page.id);
}

export { TURN_INTO };
